import { useCallback, useState } from "react";
import { PanelSection } from "../PanelSection";
import { useTranslate } from "@refinedev/core";
import {
  ITEM_TYPES,
  MediaAssetsViewOptions,
  MediaAssetTabs,
  MediaKind,
} from "../../../types";
import { ConfigProvider, Flex, Tooltip } from "antd";
import { AlertCard } from "@libertify/ui";
import { useAntTheme } from "hooks/useAntTheme";
import { ImagesTab } from "../ImagesVideosTabs/ImagesTab";
import { VideosTab } from "../ImagesVideosTabs/VideosTab";
import { SegmentedButtons } from "../../controls/SegmentedButtons";
import { useTemplateEditorStore } from "../../../store/useTemplateEditorStore";
import { useTemplatesStatusStore } from "../../../store/useTemplatesStatusStore";
import { useTemplateData } from "../../../hooks/useTemplateData";
import { ImageVideoControls } from "../../common/ImageVideoControls";
import { BackgroundControls } from "./BackgroundControls";
import { SelectedItemSection } from "../SelectedItemSection";

export const MediaTab = ({
  previewWidth,
  previewHeight,
}: {
  previewWidth: number;
  previewHeight: number;
}) => {
  const t = useTranslate();
  const { theme } = useAntTheme();

  const {
    templatesData: { isPromptProject },
  } = useTemplateData();

  const {
    mainImage,
    updateMainImage,
    updateImage,
    updateVideo,
    images,
    videos,
    currentItem,
  } = useTemplateEditorStore((state) => ({
    mainImage: state.templateState.mainImage,
    updateMainImage: state.updateMainImage,
    updateImage: state.updateImage,
    updateVideo: state.updateVideo,
    images: state.templateState.images,
    videos: state.templateState.videos,
    currentItem: state.templateState.currentItem,
  }));

  const { addTemplateUpdate } = useTemplatesStatusStore((state) => ({
    addTemplateUpdate: state.addTemplateUpdate,
  }));

  const isMediaItemSelected =
    currentItem?.type === ITEM_TYPES.Image ||
    currentItem?.type === ITEM_TYPES.Video;
  const isBackgroundSelected = currentItem?.type === ITEM_TYPES.Background;

  const [activeTab, setActiveTab] = useState<MediaAssetTabs>(
    MediaAssetTabs.Images
  );
  const [mediaAssetTab, setMediaAssetTab] = useState<MediaAssetsViewOptions>(
    MediaAssetsViewOptions.Stock
  );

  // Replacing the asset of the selected canvas item is enabled whenever an
  // image/video item is selected. Clicking a gallery thumbnail in that mode
  // swaps the selected item's asset instead of adding a new one. The main image
  // is just one of these items (it additionally supports image↔video swaps).
  const isMainImageSelected =
    !!mainImage?.id && currentItem?.id === mainImage.id;
  const selectedRegularImage =
    currentItem?.type === ITEM_TYPES.Image &&
    currentItem.id &&
    !isMainImageSelected
      ? images.find((image) => image.id === currentItem.id)
      : undefined;
  const selectedRegularVideo =
    currentItem?.type === ITEM_TYPES.Video && currentItem.id
      ? videos.find((video) => video.id === currentItem.id)
      : undefined;
  const isReplaceableItemSelected =
    isMainImageSelected || !!selectedRegularImage || !!selectedRegularVideo;

  const onReplaceSelectedAsset = useCallback(
    (assetId: string, path: string, mediaType: MediaKind) => {
      if (isMainImageSelected) {
        updateMainImage({ assetId, path, mediaType, visible: true });
      } else if (selectedRegularImage) {
        updateImage({ id: selectedRegularImage.id, assetId, path });
      } else if (selectedRegularVideo) {
        updateVideo({ id: selectedRegularVideo.id, assetId, path });
      }
      addTemplateUpdate();
    },
    [
      isMainImageSelected,
      selectedRegularImage,
      selectedRegularVideo,
      updateMainImage,
      updateImage,
      updateVideo,
      addTemplateUpdate,
    ]
  );

  // Forward the replace callback only to the matching tab kind: the Images tab
  // replaces images (and the main image), the Videos tab replaces videos (and
  // the main image, which supports cross-kind swaps).
  const onReplaceFromImagesTab =
    isMainImageSelected || selectedRegularImage
      ? onReplaceSelectedAsset
      : undefined;
  const onReplaceFromVideosTab =
    isMainImageSelected || selectedRegularVideo
      ? onReplaceSelectedAsset
      : undefined;

  let mediaAssetTabItems = Object.values(MediaAssetsViewOptions);
  if (activeTab === MediaAssetTabs.Videos) {
    // TODO: re-enable AI generated videos once we know which asset library
    // contains the project's generated videos (need to confirm with backend).
    mediaAssetTabItems = mediaAssetTabItems.filter(
      (item) =>
        item !== MediaAssetsViewOptions.Document &&
        item !== MediaAssetsViewOptions.AIGenerated
    );
  }
  if (isPromptProject) {
    mediaAssetTabItems = mediaAssetTabItems.filter(
      (item) => item !== MediaAssetsViewOptions.Document
    );
  }

  // If the currently selected option is no longer in the list (e.g. user
  // switched from Images to Videos and the previous selection isn't allowed),
  // fall back to Stock.
  if (!mediaAssetTabItems.includes(mediaAssetTab)) {
    setMediaAssetTab(MediaAssetsViewOptions.Stock);
  }

  const mediaAssetTabItemsWithTitles = mediaAssetTabItems.map((value) => ({
    option: value,
    title: t(`components.templates.settings.mediaTab.${value}`, {
      type: activeTab,
    }),
  }));

  const topItems: { key: MediaAssetTabs; label: string }[] = [
    {
      key: MediaAssetTabs.Images,
      label: t("asset_libraries.images"),
    },
    {
      key: MediaAssetTabs.Videos,
      label: t("asset_libraries.videos"),
    },
  ];

  return (
    <Flex vertical style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
      {isMediaItemSelected && (
        <ImageVideoControls
          previewWidth={previewWidth}
          previewHeight={previewHeight}
        />
      )}
      {isBackgroundSelected && (
        <SelectedItemSection
          title={t("components.templates.settings.bodyColor")}
        >
          <BackgroundControls />
        </SelectedItemSection>
      )}
      <PanelSection
        style={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          justifyContent: "start",
        }}
      >
        <ConfigProvider
          theme={{
            components: {
              Radio: {
                buttonBg: "transparent",
                buttonSolidCheckedBg: theme.colorWhite,
                buttonSolidCheckedColor: theme.colorTextBase,
                colorBorder: "transparent",
                buttonSolidCheckedHoverBg: theme.colorWhite,
                buttonColor: theme.colorTextSecondary,
              },
            },
          }}
        >
          <SegmentedButtons
            options={topItems.map((item) => ({
              label: item.label.toUpperCase(),
              value: item.key,
              tooltip: t(`components.templates.settings.mediaTab.${item.key}`),
            }))}
            value={activeTab}
            onChange={(value) => {
              setActiveTab(value);
              setMediaAssetTab(MediaAssetsViewOptions.Stock);
            }}
            style={{ width: "100%", maxWidth: 400, margin: "0 auto" }}
          />
          {isReplaceableItemSelected && (
            <AlertCard
              type="info"
              title={t(
                "components.templates.settings.mediaTab.clickToChangeMedia"
              )}
            />
          )}
          <SegmentedButtons
            value={mediaAssetTab}
            options={mediaAssetTabItemsWithTitles.map((item) => ({
              label: (
                <Tooltip title={item.title}>
                  {item.option.replaceAll("_", " ").toUpperCase()}
                </Tooltip>
              ),
              value: item.option,
            }))}
            onChange={(value) => setMediaAssetTab(value)}
          />
          {activeTab === MediaAssetTabs.Images && (
            <ImagesTab
              previewWidth={previewWidth}
              previewHeight={previewHeight}
              mediaAssetTabView={mediaAssetTab}
              onReplaceSelectedAsset={onReplaceFromImagesTab}
            />
          )}
          {activeTab === MediaAssetTabs.Videos && (
            <VideosTab
              previewWidth={previewWidth}
              previewHeight={previewHeight}
              mediaAssetTabView={mediaAssetTab}
              onReplaceSelectedAsset={onReplaceFromVideosTab}
            />
          )}
        </ConfigProvider>
      </PanelSection>
    </Flex>
  );
};
